import { FC, useEffect } from 'react';
import { useAreas } from './useAreas';
import { getMaskImage } from './getMaskImage';
import { AreaBorders } from './AreaBorders';
import { HoleParams } from './types';
import styles from './background.module.scss';

interface BackgroundProps {
  containerRef?: React.RefObject<HTMLElement | null>;
  holes?: HoleParams[];
  clickable?: string;
  onClick?: (event: MouseEvent) => void;
}

export const Background: FC<BackgroundProps> = ({
  containerRef,
  holes,
  clickable,
  onClick,
}) => {
  const areas = useAreas(holes);

  const container = containerRef?.current ?? undefined;
  const maskImage =
    container && areas.length ? getMaskImage(areas, container) : undefined;

  const containerRect = container?.getBoundingClientRect();

  useEffect(() => {
    const handler = (event: MouseEvent) => {
      const target = event.target as HTMLElement;

      if (containerRef?.current) {
        const rect = containerRef.current.getBoundingClientRect();

        const insideContainer =
          event.clientX >= rect.left &&
          event.clientX <= rect.right &&
          event.clientY >= rect.top &&
          event.clientY <= rect.bottom;

        if (!insideContainer) return;
      }

      const isInside = (selector: string) =>
        Array.from(document.querySelectorAll(selector)).some(
          (el) => el === target || el.contains(target),
        );

      const alwaysClickable =
        '[data-tutorial="fullscreen-window"], [data-tutorial="language-picker"], [data-tutorial="tutorial"], .fullscreen-dialog, .Toastify__toast-container button, .wrapper';

      if (isInside(alwaysClickable)) return;

      if (clickable && isInside(clickable)) {
        onClick?.(event);
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();
    };

    document.addEventListener('click', handler, { capture: true });
    return () => document.removeEventListener('click', handler, { capture: true });
  }, [clickable, containerRef, onClick]);

  return (
    <div className={styles.areas} style={maskImage ? { maskImage } : undefined}>
      {containerRect &&
        areas.map((area, i) => (
          <AreaBorders key={i} area={area} containerRect={containerRect} />
        ))}
    </div>
  );
};
